import { Suspense, useMemo } from 'react';
import { PREMATCH_GAME_TYPE_FOR_SWARM } from 'utils/constants/sportsbook/data-fields';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy } from 'utils/generic/lazy';
import { isMobile } from 'utils/is-mobile';
import { getLivePreMatchSportTypes } from 'utils/sportsbook/sport-data';
import { EAdvancedEventsLayouts } from 'interfaces/sportsbook';
import {
  TFilteredCouponLayoutsForRanges,
  TIME_RANGES_BY_COUPON_LAYOUT
} from 'newelements/Sportsbook/layouts/advanced/constants/coupon-time-ranges';
import { getGameWhereByLayoutType } from 'newelements/Sportsbook/layouts/advanced/helpers/getGameWhereByLayoutType';
import { MenuUnderlineSkeleton } from 'newcomponents/UI/Menu/layouts/underline/skeleton';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useSearchParams } from 'hooks/useSearchParams';

const { SportsbookSportsMenu } = lazy(
  () =>
    import(
      /* webpackChunkName: "sportsbook-sports-menu" */ 'newelements/Sportsbook/__partials/SportsbookSportsMenu'
    )
);

const { SportsMenuWithFavorite } = lazy(
  () =>
    import(
      /* webpackChunkName: "sportsbook-sports-menu-with-favorite" */ 'newelements/Sportsbook/__partials/SportsbookSportsMenu/SportsMenuWithFavorite'
    )
);

export const AdvancedMainSportsMenu = () => {
  const {
    layoutType: selectedLayoutInQuery,
    coupon: selectedCouponInQuery
  }: {
    layoutType?: EAdvancedEventsLayouts;
    coupon?: TFilteredCouponLayoutsForRanges;
  } = useSearchParams();

  const configs = useSportsbookConfigs();

  const whereParams = useMemo(() => {
    if (SpringConfigs.MOCKED_DATA) {
      return null;
    }

    if (selectedLayoutInQuery) {
      return {
        game: getGameWhereByLayoutType({ layoutType: selectedLayoutInQuery })
      };
    }

    if (selectedCouponInQuery) {
      const ranges = TIME_RANGES_BY_COUPON_LAYOUT[selectedCouponInQuery];

      return {
        game: {
          start_ts: ranges,
          ...PREMATCH_GAME_TYPE_FOR_SWARM
        }
      };
    }

    return {
      sport: {
        type: {
          '@in': getLivePreMatchSportTypes(configs.sportType)
        }
      }
    };
  }, [selectedLayoutInQuery, selectedCouponInQuery]);

  return (
    <Suspense fallback={<MenuUnderlineSkeleton />}>
      {isMobile() ? (
        <SportsMenuWithFavorite whereParams={whereParams} />
      ) : (
        <SportsbookSportsMenu whereParams={whereParams} />
      )}
    </Suspense>
  );
};
